import { darkInkColorFor, fadeColor, standardColor } from 'js/utils/color';
import { FIELD_DELIMITER, standardCollator } from 'js/utils/string';
import { NA } from 'js/utils/question-set';
import {
  decoratedOptions,
  isLiveSentimentQuestion,
  normalizedValue,
  resolveLiveQuestionType,
  sentimentColor,
  sentimentDomain,
} from 'js/utils/sentiment-question';

export const TILE_PRESENTATION = Object.freeze({
  SCORED_DISTRIBUTION: 'scored-distribution',
  CHOICE_DISTRIBUTION: 'choice-distribution',
  COMPLETION: 'completion',
});

/** Same green as a good traffic stop, used here to mean "answered", not scored. */
const GOOD_GREEN = '#4c9745';

/** `$purple` and its dark sibling from the category palette. */
const UNSCORED_PURPLE = '#d38fc7';
const UNSCORED_PURPLE_INK = '#712563';

export const ANSWERED_TILE = Object.freeze({
  wash: fadeColor(GOOD_GREEN),
  washInk: darkInkColorFor(GOOD_GREEN),
});

/** Group results for unscored questions that have responses, not a traffic score. */
export const RESPONDED_TILE = Object.freeze({
  wash: fadeColor(UNSCORED_PURPLE),
  washInk: UNSCORED_PURPLE_INK,
});

/**
 * How a live result tile should present this question. Not the same as
 * scored-vs-not-scored: choice questions still show a distribution.
 */
export function liveTilePresentation(question) {
  const type = resolveLiveQuestionType(question);

  if (type === 'single-select' || type === 'multi-select') {
    return TILE_PRESENTATION.CHOICE_DISTRIBUTION;
  }
  if (type === 'long-text') {
    return TILE_PRESENTATION.COMPLETION;
  }
  return TILE_PRESENTATION.SCORED_DISTRIBUTION;
}

/**
 * Ballot order for a non-sentiment live question: authoring order (`ordinal`,
 * then fractional `p`), not value rank. Sentiment reverses after decorating.
 */
export function orderedLiveChoices(question) {
  return [...(question?.answers ?? [])]
    .filter((answer) => !answer?.is_na)
    .sort((a, b) => {
      if (a.ordinal != null && b.ordinal != null && a.ordinal !== b.ordinal) {
        return a.ordinal - b.ordinal;
      }
      return standardCollator.compare(String(a.p ?? ''), String(b.p ?? ''));
    });
}

/**
 * The mean of a question's responses mapped onto 0..1 against that question's
 * own answer domain. Pass the result to `sentimentColor` or `trafficBucketFor`.
 * Returns `null` when there is nothing to average, so callers can render an
 * explicit "no results" state rather than a misleading color.
 */
export function getNormalizedQuestionScore(question) {
  if (
    liveTilePresentation(question) !== TILE_PRESENTATION.SCORED_DISTRIBUTION
  ) {
    return null;
  }

  const domain = sentimentDomain(question?.answers);
  const fractions = (question?.responses ?? [])
    .filter((r) => !r.is_na)
    .map((r) => normalizedValue(r.value, domain))
    .filter((f) => f != null);

  if (fractions.length === 0) {
    return null;
  }

  return fractions.reduce((sum, f) => sum + f, 0) / fractions.length;
}

/**
 * Every one of a participant's responses across an assessment, normalized
 * against each question's own domain so questions with different option counts
 * can be averaged together.
 */
export function getNormalizedParticipantScores(assessment, participantId) {
  return flattenQuestions(assessment).map((question) => {
    if (
      liveTilePresentation(question) !== TILE_PRESENTATION.SCORED_DISTRIBUTION
    ) {
      return null;
    }

    const response = (question.responses ?? []).find(
      (r) => r.participant_id === participantId
    );

    if (!response || response.is_na) {
      return null;
    }

    return normalizedValue(response.value, sentimentDomain(question.answers));
  });
}

/**
 * Every question in the assessment as one ordered list.
 *
 * A live assessment's question sets are an authoring convenience only: the host
 * and participants walk a single sequence of questions, so anything that
 * presents or aggregates questions at runtime should flatten first.
 */
export function flattenQuestions(assessment) {
  return (assessment?.question_sets ?? []).flatMap((qs) => qs.questions ?? []);
}

export function liveNaChoice(question) {
  return (question?.answers ?? []).find((answer) => answer?.is_na) ?? null;
}

export function isBlankComment(value) {
  return String(value ?? '').trim() === '';
}

/**
 * Resolve the answer row to persist for a live vote. Multi-select keeps the
 * joined `value` and takes `answer_id` from the first selected choice.
 */
export function matchLiveVoteAnswer(question, value) {
  if (value === NA) {
    const na = liveNaChoice(question);
    return {
      id: na?.id ?? na?.guid ?? null,
      value: na?.value ?? null,
      is_na: true,
    };
  }

  const answers = question?.answers ?? [];
  const exact = answers.find(
    (answer) => !answer?.is_na && String(answer.value) === String(value)
  );
  if (exact) {
    return {
      id: exact.id ?? exact.guid ?? null,
      value: exact.value,
      is_na: false,
    };
  }

  const keys = String(value).split(FIELD_DELIMITER).filter(Boolean);
  const first = answers.find(
    (answer) => !answer?.is_na && keys.includes(String(answer.value))
  );

  return {
    id: first?.id ?? first?.guid ?? null,
    value,
    is_na: false,
  };
}

export function participantHasUnscoredResponse(assessment, participantId) {
  return flattenQuestions(assessment).some((question) => {
    if (
      liveTilePresentation(question) === TILE_PRESENTATION.SCORED_DISTRIBUTION
    ) {
      return false;
    }

    const response = (question.responses ?? []).find(
      (r) => r.participant_id === participantId
    );
    return Boolean(
      response &&
        (response.is_na || (response.value != null && response.value !== ''))
    );
  });
}

/** Background color for a list sparkline dot, or null when the cell is empty. */
export function sparklineWash(question) {
  const score = getNormalizedQuestionScore(question);
  if (score != null) {
    return sentimentColor(score);
  }
  if (
    liveTilePresentation(question) === TILE_PRESENTATION.SCORED_DISTRIBUTION
  ) {
    return null;
  }

  const has = (question.responses ?? []).some(
    (r) => r && !r.is_na && r.value != null && r.value !== ''
  );
  return has ? RESPONDED_TILE.wash : null;
}

export function selectedLiveChoiceValues(question, response) {
  if (response?.value == null || response.value === '') {
    return [];
  }

  const raw = String(response.value);
  if (resolveLiveQuestionType(question) === 'multi-select') {
    return raw.split(FIELD_DELIMITER).filter(Boolean);
  }

  return [raw];
}

function tallyOptions(question) {
  if (isLiveSentimentQuestion(question)) {
    return [...decoratedOptions(question)].reverse();
  }
  if (
    liveTilePresentation(question) === TILE_PRESENTATION.SCORED_DISTRIBUTION
  ) {
    return decoratedOptions(question);
  }
  return orderedLiveChoices(question);
}

/**
 * One tally bar per scored choice. Sentiment bars are highest-value first with
 * the same traffic ramp as the vote buttons. Likert bars keep value order and
 * the traffic ramp. Single-select bars follow ballot order and use the shared
 * categorical color rather than a scale.
 */
export function getChoiceTallyData(question, responses = []) {
  const isScored =
    liveTilePresentation(question) === TILE_PRESENTATION.SCORED_DISTRIBUTION;
  const options = tallyOptions(question);
  const counts = new Map(options.map((option) => [String(option.value), 0]));
  const fallbackColor = standardColor();

  responses.forEach((response) => {
    if (response?.is_na) {
      return;
    }
    selectedLiveChoiceValues(question, response).forEach((key) => {
      if (counts.has(key)) {
        counts.set(key, counts.get(key) + 1);
      }
    });
  });

  return options.map((option) => ({
    name: String(option.value),
    count: counts.get(String(option.value)) ?? 0,
    color: isScored ? option.color : fallbackColor,
    text: option.text,
  }));
}
